import { ChainId } from '@nominex/nomiswap-sdk'
import { BigNumber } from '@ethersproject/bignumber'
import { getPoolsConfig, PoolCategory } from 'config/pools'
import multicall from 'utils/multicall'
import { getBoostingServiceContract, getStakingServiceContract } from 'utils/contractHelpers'
import boostingServiceAbi from 'config/abi/IPrivateBoostingServiceContract.json'

const BOOSTING_INFO_METHODS = [
  'DURATION',
  'boostingRate',
  'historicalBoostingRate',
  'historicalCompoundRate',
  'paused',
  'penaltyRate',
  'totalShares',
  'totalStakedCompounded',
  'totalBoostings',
]

const PRIVATE_BOOSTING_INFO_METHODS = ['unstakedShares']

export const fetchPoolsBoostingInfo = async (chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const poolsCalls = poolsConfig
    .filter(({ poolCategory }) => poolCategory === PoolCategory.BOOSTING)
    .map(async ({ contractAddress, sousId, isPrivate }) => {
      const infoCalls = BOOSTING_INFO_METHODS.map((name) => ({
        address: contractAddress,
        name,
      }))

      if (isPrivate) {
        infoCalls.push(
          ...PRIVATE_BOOSTING_INFO_METHODS.map((name) => ({
            address: contractAddress,
            name,
          })),
        )
      }

      const info = (await multicall(boostingServiceAbi, infoCalls, chainId)).reduce((acc, [result], index) => {
        let fieldName = infoCalls[index].name
        if (fieldName === 'DURATION') {
          fieldName = 'duration'
        }
        acc[fieldName] = BigNumber.isBigNumber(result) ? result.toString() : result
        return acc
      }, {})

      return { info, sousId }
    })

  return Promise.all(poolsCalls)
}

export const fetchPoolsTotalStaking = async (chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const poolsCalls = poolsConfig.map(({ poolCategory, contractAddress }) => {
    switch (poolCategory) {
      case PoolCategory.CORE:
        return getStakingServiceContract(contractAddress, chainId).totalStaked()
      case PoolCategory.BOOSTING:
        return getBoostingServiceContract(contractAddress).totalStaked()
      default:
        return Promise.resolve(0)
    }
  })

  const totalStaked = await Promise.all(poolsCalls)

  return poolsConfig.map(({ sousId }, index) => ({
    sousId,
    totalStaked: totalStaked[index].toString(),
  }))
}

export const fetchLaunchpoolsTotalStaking = async (chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const launchpoolsAddresses = poolsConfig.reduce((acc, { launchpoolAddress }) => {
    if (!acc.includes(launchpoolAddress)) {
      acc.push(launchpoolAddress)
    }
    return acc
  }, [])

  const launchpoolsCalls = launchpoolsAddresses.map(async (launchpoolsAddress) => ({
    totalStaked: (await getStakingServiceContract(launchpoolsAddress, chainId).totalStaked()).toString(),
    address: launchpoolsAddress,
  }))

  return Promise.all(launchpoolsCalls)
}
